import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Autocomplete, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Axios } from "api/axios";
import BootstrapTextField from "Common/BootstrapTextField";
import { errorFeedbackStyle } from "Common/errorFeedbackStyle";
import SubmitButton from "Components/Buttons/SubmitButton";
import { autocompleteDropdownStyles } from "Constants/Style";
import { City, Country, State } from "country-state-city";
import { useHandleBack } from "helpers/functions/useHandleBack";
import "react-country-state-city/dist/react-country-state-city.css";
import { fetchALLUserFormDropdowns } from "slices/users/thunk";
import UserForm from "./UserForm";

interface BasicDetailsProps {
  setActiveTab: (tabName: string) => void;
}

const BasicDetails: React.FC<BasicDetailsProps> = ({ setActiveTab }) => {
  const location = useLocation();
  const { user_data } = location.state || {};
  const user_id = user_data?.user_id || "";
  const local_data = JSON.parse(localStorage.getItem("authUser") || "{}") || {};

  const [countries, setCountries] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const payload = {
    organization: "",
    department: "",
    team: "",
    first_name: "",
    last_name: "",
    username: "",
    designation: "",
    user_type: "",
    email: "",
    password: "",
    password2: "",
    mobile_no: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
  };

  const [formData, setFormData] = useState<{ [key: string]: string }>(payload);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [teamOptions, setTeamOptions] = useState<any[]>([]);
  const [nextPage, setNextPage] = useState(false);
  const [isPermissionsEnabled, setIsPermissionsEnabled] = useState(false);

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const handleBack = useHandleBack("/user-list");

  const { userTypes, organizations, departments, teams } = useSelector(
    (state: any) => state.users.userFormDropDowns
  );

  const commonInputStyle = {
    fontSize: "0.8rem",
    minHeight: 38,
    padding: "0.375rem 0.75rem",
  };

  const commonLabelStyle = {
    fontSize: "0.8rem",
    marginBottom: 6,
    marginTop: 12,
  };

  const containerStyle = {
    marginTop: "2rem",
    marginLeft: 0,
    marginRight: 0,
    padding: "2rem 1rem",
    width: "100%",
    borderRadius: "0.75rem",
  };

  console.log(formData);

  const cardStyle = { padding: "2rem 1rem", borderRadius: "0.75rem" };

  useEffect(() => {
    dispatch(fetchALLUserFormDropdowns());
  }, [dispatch]);

  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);
    
    // Auto-select India for new user creation (when user_id is empty)
    if (!user_id) {
      const india = countryList.find((country: any) => country.name === "India");
      if (india) {
        setFormData((prev) => {
          // Only set if country is not already set
          if (!prev.country) {
            // Load states for India
            const indiaStates = State.getStatesOfCountry(india.isoCode);
            setStates(indiaStates);
            
            return {
              ...prev,
              country: india.name,
            };
          }
          return prev;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id]);

  useEffect(() => {
    if (formData?.organization !== "") {
      Axios.get(`/api/user/department-dropdown/${formData?.organization}/`)
        .then((res: any) => {
          if (res?.data) {
            setDepartmentOptions(res?.data?.data || []);
          } else {
            setDepartmentOptions([]);
          }
        })
        .catch((error: any) => {
          console.error("Failed to load departments:", error);
          setDepartmentOptions([]);
          // toast.error("Failed to load departments. Please try again.");
        });
    }
  }, [formData?.organization]);

  useEffect(() => {
    if (formData?.department !== "") {
      Axios.get(
        `/api/user/team-dropdown/${formData?.organization}/${formData?.department}/`
      )
        .then((res: any) => {
          if (res?.data) {
            setTeamOptions(res?.data?.data || []);
          } else {
            setTeamOptions([]);
          }
        })
        .catch((error: any) => {
          console.error("Failed to load teams:", error);
          setTeamOptions([]);
          // toast.error("Failed to load teams. Please try again.");
        });
    }
  }, [formData?.department]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    // if (!formData.organization)
    //   newErrors.organization = "Organisation is required";
    // if (!formData.department) newErrors.department = "Department is required";
    // if (!formData.team) newErrors.team = "Team is required";
    if (!formData.first_name) newErrors.first_name = "First name is required";
    if (!formData.last_name) newErrors.last_name = "Last name is required";
    if (!formData.username) newErrors.username = "Username is required";
    // if (!formData.designation)
    //   newErrors.designation = "Designation is required";
    if (!formData.user_type) newErrors.user_type = "User type is required";

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    // Password validation only for create mode
    if (!user_id) {
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      }

      if (!formData.password2) {
        newErrors.password2 = "Confirm password is required";
      } else if (
        formData.password &&
        formData.password !== formData.password2
      ) {
        newErrors.password2 = "Passwords do not match. Please re-enter.";
      }
    }

    if (!formData.mobile_no) {
      newErrors.mobile_no = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.mobile_no)) {
      newErrors.mobile_no = "Mobile No. must be 10 digits";
    }

    // if (!formData.address) newErrors.address = "Address is required";
    // if (!formData.country) newErrors.country = "Country is required";
    // if (!formData.state) newErrors.state = "State is required";
    // if (!formData.city) newErrors.city = "City is required";

    // if (!formData.pincode) {
    //   newErrors.pincode = "Pincode is required";
    // } else if (!/^\d{6}$/.test(formData.pincode)) {
    //   newErrors.pincode = "Pincode must be 6 digits";
    // }

    return newErrors;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Enhanced password change handler with real-time validation
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Real-time validation for password match
      if (name === "password" || name === "password2") {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };

          // Clear password errors when typing
          if (name === "password") {
            delete newErrors.password;
          }

          // Check if passwords match in real-time
          if (name === "password2") {
            if (value && updated.password !== value) {
              newErrors.password2 = "Passwords do not match";
            } else {
              delete newErrors.password2;
            }
          }

          // Re-validate password2 when password changes
          if (name === "password" && updated.password2) {
            if (value !== updated.password2) {
              newErrors.password2 = "Passwords do not match";
            } else {
              delete newErrors.password2;
            }
          }

          return newErrors;
        });
      }

      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      // Show specific error message for password mismatch
      if (validationErrors.password2?.includes("do not match")) {
        toast.error(
          "Passwords do not match! Please ensure both password fields are identical.",
          {
            autoClose: 5000,
          }
        );
      } else if (validationErrors.password || validationErrors.password2) {
        toast.error("Please enter all required fields correctly.", {
          autoClose: 4000,
        });
      } else {
        toast.error("Please enter all required fields correctly.", {
          autoClose: 3000,
        });
      }
      setErrors(validationErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      localStorage.removeItem("permission_user");
      let response;
      let payload = { ...formData };

      if (user_id) {
        payload.created_by = local_data?.username;
        payload.updated_by = local_data?.username;
        payload.organization = user_data?.organization;

        // CRITICAL FIX: Remove password fields from edit payload
        delete payload.password;
        delete payload.password2;

        // Filter out empty fields
        payload = Object.fromEntries(
          Object.entries(payload).filter(([_, v]) => v !== "")
        );
        response = await Axios.put(`api/user/edit/${user_id}/`, payload);
      } else {
        response = await Axios.post("api/user/create/", formData);
      }

      if (response.status === 200 || response.status === 201) {
        toast.success(`User ${user_id ? "updated" : "created"} successfully`);
        setTimeout(() => {
          if (user_id) {
            // Edit mode: Just refresh the page to show updated data
            // window.location.reload();
          } else {
            // Create mode: Switch to permissions tab
            localStorage.setItem(
              "permission_user",
              JSON.stringify(response?.data?.data?.user_id)
            );
            setIsPermissionsEnabled(true);
            setNextPage(true);
            setActiveTab("permissions");
          }
        }, 1000);
      } else {
        toast.error(`Failed to ${user_id ? "update" : "create"} user`);
      }
    } catch (error: any) {
      let errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to save user";

      if (
        typeof errorMsg === "string" &&
        errorMsg.includes("user with this email already exists")
      ) {
        toast.error("User with this email already exists");
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((prev) => !prev);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (user_data && userTypes.length > 0) {
      const allCountries = Country.getAllCountries();
      const selectedCountry = allCountries.find(
        (c) => c.name === user_data.country
      );
      const countryIsoCode = selectedCountry?.isoCode || "";
      const statesOfCountry = State.getStatesOfCountry(countryIsoCode);
      const selectedState = statesOfCountry.find(
        (s) => s.name === user_data.state
      );
      const stateIsoCode = selectedState?.isoCode || "";
      const citiesOfState = City.getCitiesOfState(countryIsoCode, stateIsoCode);

      setCountries(allCountries);
      setStates(statesOfCountry);
      setCities(citiesOfState);

      setFormData({
        organization: user_data?.organization || "",
        department: user_data?.department || "",
        team: user_data?.team || "",
        first_name: user_data?.first_name || "",
        last_name: user_data?.last_name || "",
        username: user_data?.username || "",
        user_type: user_data?.user_type || "",
        email: user_data?.email || "",
        designation: user_data?.designation || "",
        password: "",
        password2: "",
        mobile_no: user_data?.mobile_no || "",
        address: user_data?.address || "",
        city: user_data?.city || "",
        state: user_data?.state || "",
        country: user_data?.country || "",
        pincode: user_data?.pincode || "",
      });
    }
  }, [user_data, userTypes]);

  // Auto-select first Organization
  useEffect(() => {
    if (organizations.length > 0 && !formData.organization) {
      setFormData((prev) => ({
        ...prev,
        organization: organizations[0].organization_id,
        department: "",
        team: "",
      }));
    }
  }, [organizations]);

  // Auto-select first Department (after organization is selected)
  useEffect(() => {
    if (departmentOptions.length > 0 && !formData.department) {
      setFormData((prev) => ({
        ...prev,
        department: departmentOptions[0].department_id,
        team: "",
      }));
    }
  }, [departmentOptions]);

  // Auto-select first Team (after department is selected)
  useEffect(() => {
    if (teamOptions.length > 0 && !formData.team) {
      setFormData((prev) => ({
        ...prev,
        team: teamOptions[0].team_id,
      }));
    }
  }, [teamOptions]);

  return (
    <div>
      {nextPage ? (
        <UserForm page={"permissions"} />
      ) : (
        <div
          className="add-organization-container bg-white"
          style={containerStyle}
        >
          {/* Header Section */}
          <div className="">
            <div className="d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
              <div className="d-flex align-items-center">
                <span
                  style={{
                    cursor: "pointer",
                    fontSize: "1.5rem",
                    lineHeight: 1,
                  }}
                  onClick={handleBack}
                >
                  <i className="bi bi-arrow-left"></i>
                </span>
                <h5 className="mb-0 fw-bold ms-2">
                  {user_id ? "Update" : "Create"} User
                </h5>
              </div>
              <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                <span
                  style={{
                    color: "inherit",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                  onClick={handleBack}
                >
                  User
                </span>{" "}
                &gt;{" "}
                <span className="text-muted">
                  {user_id ? "Edit User" : "Add User"}
                </span>
              </div>
            </div>
          </div>

          <Card className="shadow-sm" style={cardStyle}>
            <Form noValidate onSubmit={handleSubmit}>
              <Row className="mb-4">
                {/* Organisation */}
                {/* <Col md={4} xs={12} className="mb-3 mb-md-0">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Organisation
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={organizations}
                    getOptionLabel={(option: any) =>
                      option.organization_name || ""
                    }
                    value={
                      organizations.find(
                        (org: any) =>
                          org.organization_id === formData.organization
                      ) || null
                    }
                    onChange={(_, value) => {
                      setFormData((prev) => ({
                        ...prev,
                        organization: value?.organization_id || "",
                        department: "",
                        team: "",
                      }));
                      setErrors((prev) => ({ ...prev, organization: "" }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Organisation"
                        error={!!errors.organization}
                        helperText={errors.organization}
                        fullWidth
                        size="small"
                        autoComplete="off"
                        InputProps={{
                          ...params.InputProps,
                          style: commonInputStyle,
                        }}
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          style: commonLabelStyle,
                        }}
                      />
                    )}
                  />
                </Col> */}

                {/* Department */}
                {/* <Col md={4} xs={12} className="mb-3 mb-md-0">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Department
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={departmentOptions}
                    getOptionLabel={(option: any) =>
                      option.department_name || ""
                    }
                    value={
                      departmentOptions?.find(
                        (dept: any) =>
                          dept?.department_id === formData?.department
                      ) || null
                    }
                    onChange={(_, value) => {
                      setFormData((prev) => ({
                        ...prev,
                        department: value?.department_id || "",
                        team: "",
                      }));
                      setErrors((prev) => ({ ...prev, department: "" }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Department"
                        error={!!errors.department}
                        helperText={errors.department}
                        fullWidth
                        size="small"
                        autoComplete="off"
                        InputProps={{
                          ...params.InputProps,
                          style: commonInputStyle,
                        }}
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          style: commonLabelStyle,
                        }}
                      />
                    )}
                  />
                </Col> */}

                {/* Team */}
                {/* <Col md={4} xs={12}>
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Team
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={teamOptions}
                    getOptionLabel={(option: any) => option.team_name || ""}
                    value={
                      teamOptions?.find(
                        (team: any) => team?.team_id === formData?.team
                      ) || null
                    }
                    onChange={(_, value) => {
                      setFormData((prev) => ({
                        ...prev,
                        team: value?.team_id || "",
                      }));
                      setErrors((prev) => ({ ...prev, team: "" }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Team"
                        error={!!errors.team}
                        helperText={errors.team}
                        fullWidth
                        size="small"
                        autoComplete="off"
                        InputProps={{
                          ...params.InputProps,
                          style: commonInputStyle,
                        }}
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          style: commonLabelStyle,
                        }}
                      />
                    )}
                  />
                </Col> */}
              </Row>

              <Row className="mb-4">
                <Col md={4} xs={12} className="mb-3 mb-md-0">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    First Name
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="first_name"
                    placeholder="Enter First Name"
                    value={formData.first_name}
                    onChange={handleChange}
                    isInvalid={!!errors.first_name}
                    size="sm"
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback type="invalid" style={errorFeedbackStyle}>
                    {errors.first_name}
                  </Form.Control.Feedback>
                </Col>

                <Col md={4} xs={12} className="mb-3 mb-md-0">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Last Name
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="last_name"
                    placeholder="Enter Last Name"
                    value={formData.last_name}
                    onChange={handleChange}
                    isInvalid={!!errors.last_name}
                    size="sm"
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback type="invalid" style={errorFeedbackStyle}>
                    {errors.last_name}
                  </Form.Control.Feedback>
                </Col>

                <Col md={4} xs={12} className="mb-3 mb-md-0">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    User Name
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    placeholder="Enter User Name"
                    value={formData.username}
                    onChange={handleChange}
                    isInvalid={!!errors.username}
                    size="sm"
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback type="invalid" style={errorFeedbackStyle}>
                    {errors.username}
                  </Form.Control.Feedback>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={4} xs={12} className="mb-3 mb-md-0">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Designation
                    {/* <span className="text-danger">*</span> */}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="designation"
                    placeholder="Enter Designation"
                    value={formData.designation}
                    onChange={handleChange}
                    isInvalid={!!errors.designation}
                    size="sm"
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.designation}
                  </Form.Control.Feedback>
                </Col>

                <Col md={4} xs={12} className="mb-3 mb-md-0">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    User Role <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={userTypes}
                    getOptionLabel={(option: any) => option.user_type || ""}
                    value={
                      userTypes.find(
                        (ut: any) => ut.user_type_id === formData.user_type
                      ) || null
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option: any) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (option: any) => option.user_type || ""
                      )
                    }
                    onChange={(_, value) => {
                      setFormData((prev) => ({
                        ...prev,
                        user_type: value?.user_type_id || "",
                      }));
                      setErrors((prev) => ({ ...prev, user_type: "" }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select User Type"
                        error={!!errors.user_type}
                        helperText={errors.user_type}
                        FormHelperTextProps={{
                          style: {
                            ...errorFeedbackStyle,
                            margin: "0.25rem 0 0 0",
                            textAlign: "left",
                            paddingLeft: "0"
                          }
                        }}
                        fullWidth
                        size="small"
                        autoComplete="off"
                        sx={autocompleteDropdownStyles.inputSx}
                        InputProps={{
                          ...params.InputProps,
                          style: commonInputStyle,
                        }}
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          style: commonLabelStyle,
                        }}
                      />
                    )}
                  />
                </Col>

                <Col md={4} xs={12} className="mb-3 mb-md-0">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Enter Email"
                    value={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                    size="sm"
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback type="invalid" style={errorFeedbackStyle}>
                    {errors.email}
                  </Form.Control.Feedback>
                </Col>
              </Row>

              <Row className="mb-4">
                {!user_id && (
                  <Col md={4} xs={12} className="mb-3 mb-md-0">
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Password <span className="text-danger">*</span>
                    </Form.Label>
                    <div style={{ position: "relative" }}>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="Enter Password (min. 8 characters)"
                        value={formData.password}
                        onChange={handlePasswordChange}
                        isInvalid={!!errors.password}
                        size="sm"
                        autoComplete="new-password"
                        style={{
                          ...commonInputStyle,
                          paddingRight: 40,
                          borderColor:
                            formData.password && !errors.password
                              ? "#28a745"
                              : undefined,
                        }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 30,
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          zIndex: 2,
                          color: "#6c757d",
                        }}
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showPassword ? (
                          <Visibility fontSize="small" />
                        ) : (
                          <VisibilityOff fontSize="small" />
                        )}
                      </span>
                    </div>
                    <Form.Control.Feedback type="invalid" style={errorFeedbackStyle}>
                      {errors.password}
                    </Form.Control.Feedback>
                  </Col>
                )}

                {!user_id && (
                  <Col md={4} xs={12} className="mb-3 mb-md-0">
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Confirm Password <span className="text-danger">*</span>
                    </Form.Label>
                    <div style={{ position: "relative" }}>
                      <Form.Control
                        type={showConfirmPassword ? "text" : "password"}
                        name="password2"
                        placeholder="Re-enter Password"
                        value={formData.password2}
                        onChange={handlePasswordChange}
                        isInvalid={!!errors.password2}
                        size="sm"
                        autoComplete="new-password"
                        style={{
                          ...commonInputStyle,
                          paddingRight: 40,
                          borderColor:
                            formData.password2 &&
                            formData.password === formData.password2
                              ? "#28a745"
                              : errors.password2
                              ? "#dc3545"
                              : undefined,
                        }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 30,
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          zIndex: 2,
                          color: "#6c757d",
                        }}
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showConfirmPassword ? (
                          <Visibility fontSize="small" />
                        ) : (
                          <VisibilityOff fontSize="small" />
                        )}
                      </span>
                      {formData.password2 &&
                        formData.password === formData.password2 &&
                        !errors.password2 && (
                          <span
                            style={{
                              position: "absolute",
                              right: 60,
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#28a745",
                              fontSize: "1.2rem",
                            }}
                          >
                            ✓
                          </span>
                        )}
                    </div>
                    <Form.Control.Feedback type="invalid" style={errorFeedbackStyle}>
                      {errors.password2}
                    </Form.Control.Feedback>
                    {formData.password2 &&
                      formData.password === formData.password2 &&
                      !errors.password2 && (
                        <div
                          style={{
                            color: "#28a745",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        ></div>
                      )}
                  </Col>
                )}

                <Col md={4} xs={12}>
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Mobile Number <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile_no"
                    placeholder="Enter Mobile Number"
                    value={formData.mobile_no}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      setFormData((prev) => ({ ...prev, mobile_no: value }));
                      setErrors((prev) => ({ ...prev, mobile_no: "" }));
                    }}
                    isInvalid={!!errors.mobile_no}
                    size="sm"
                    maxLength={10}
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback type="invalid" style={errorFeedbackStyle}>
                    {errors.mobile_no}
                  </Form.Control.Feedback>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12}>
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Address
                    {/* <span className="text-danger">*</span> */}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="address"
                    placeholder="Enter Address"
                    value={formData.address}
                    onChange={handleChange}
                    isInvalid={!!errors.address}
                    size="sm"
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.address}
                  </Form.Control.Feedback>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={4} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Country
                    {/* <span className="text-danger">*</span> */}
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={countries}
                    getOptionLabel={(option) => option.name}
                    value={
                      countries.find((c: any) => c.name === formData.country) ||
                      null
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (option) => option.name
                      )
                    }
                    onChange={(e, selectedCountry) => {
                      setFormData((prev) => ({
                        ...prev,
                        country: selectedCountry?.name || "",
                        state: "",
                        city: "",
                      }));
                      setErrors((prev) => ({ ...prev, country: "" }));

                      if (selectedCountry) {
                        const newStates = State.getStatesOfCountry(
                          selectedCountry.isoCode
                        );
                        setStates(newStates);
                        setCities([]);
                      }
                    }}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select Country"
                        size="small"
                        error={!!errors.country}
                        helperText={errors.country}
                        fullWidth
                        sx={autocompleteDropdownStyles.inputSx}
                      />
                    )}
                  />
                </Col>

                <Col md={4} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    State
                    {/* <span className="text-danger">*</span> */}
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={states}
                    getOptionLabel={(option) => option.name}
                    value={
                      states.find((s: any) => s.name === formData.state) || null
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (option) => option.name
                      )
                    }
                    onChange={(e, selectedState) => {
                      setFormData((prev) => ({
                        ...prev,
                        state: selectedState?.name || "",
                        city: "",
                      }));
                      setErrors((prev) => ({ ...prev, state: "" }));

                      const selectedCountry = countries.find(
                        (c: any) => c.name === formData.country
                      );
                      if (selectedCountry && selectedState) {
                        const newCities = City.getCitiesOfState(
                          selectedCountry.isoCode,
                          selectedState.isoCode
                        );
                        setCities(newCities);
                      }
                    }}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select State"
                        size="small"
                        error={!!errors.state}
                        helperText={errors.state}
                        fullWidth
                        sx={autocompleteDropdownStyles.inputSx}
                      />
                    )}
                    disabled={!formData.country}
                  />
                </Col>

                <Col md={4} xs={12} className="mb-3">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    City
                    {/* <span className="text-danger">*</span> */}
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    options={cities}
                    getOptionLabel={(option) => option.name}
                    value={
                      cities.find((c: any) => c.name === formData.city) || null
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (option) => option.name
                      )
                    }
                    onChange={(e, selectedCity) => {
                      setFormData((prev) => ({
                        ...prev,
                        city: selectedCity?.name || "",
                      }));
                      setErrors((prev) => ({ ...prev, city: "" }));
                    }}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select City"
                        size="small"
                        error={!!errors.city}
                        helperText={errors.city}
                        fullWidth
                        sx={autocompleteDropdownStyles.inputSx}
                      />
                    )}
                    disabled={!formData.state}
                  />
                </Col>

                <Col md={4} xs={12} className="mb-4 mb-md-0">
                  <Form.Label className="fw-semibold" style={commonLabelStyle}>
                    Pincode
                    {/* <span className="text-danger">*</span> */}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="pincode"
                    placeholder="Enter Pincode"
                    value={formData.pincode}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);
                      setFormData((prev) => ({ ...prev, pincode: value }));
                      setErrors((prev) => ({ ...prev, pincode: "" }));
                    }}
                    isInvalid={!!errors.pincode}
                    size="sm"
                    maxLength={6}
                    style={commonInputStyle}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.pincode}
                  </Form.Control.Feedback>
                </Col>
              </Row>

              <div className="d-flex justify-content-center gap-3 mt-3">
                <Button
                  variant="outline-secondary"
                  style={{
                    color: "#878899",
                    borderColor: "#B0BAC9",
                    backgroundColor: "transparent",
                    position: "relative",
                  }}
                  onClick={handleBack}
                >
                  Cancel
                </Button>

                <SubmitButton
                  isSubmitting={isSubmitting}
                  isEdit={user_id}
                  labels={{
                    create: "Next",
                    update: "Update",
                    submitting: "Submitting...",
                    updating: "Saving...",
                  }}
                />
              </div>
            </Form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BasicDetails;
